import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearCredentials } from "../../store/authSlice";
import { ROLES } from "../../utils/constants";
import AuthCard from "../../components/layout/AuthCard";
import Button from "../../components/ui/Button";
import WarningIcon from "../../components/icons/WarningIcon";

const Forbidden = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = useSelector((s) => s.auth.role);
  const token = useSelector((s) => s.auth.accessToken);

  const [secondsLeft, setSecondsLeft] = useState(15);

  useEffect(() => {
    if (secondsLeft <= 0) {
      dispatch(clearCredentials());
      navigate("/auth/login", { replace: true });
      return;
    }

    const interval = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [secondsLeft, dispatch, navigate]);

  const handleReturnToDashboard = () => {
    if (token) {
      if (role === ROLES.RECEIVER) {
        navigate("/receiver/overview", { replace: true });
      } else if (role === ROLES.SENDER) {
        navigate("/sender/invoices", { replace: true });
      } else {
        navigate("/auth/login", { replace: true });
      }
    } else {
      navigate("/auth/login", { replace: true });
    }
  };

  const handleLogoutNow = () => {
    dispatch(clearCredentials());
    navigate("/auth/login", { replace: true });
  };

  // Progress percentage (15s to 0s)
  const progressPercent = ((15 - secondsLeft) / 15) * 100;

  return (
    <AuthCard>
      <div className="flex flex-col items-center text-center space-y-6">
        <div className="w-14 h-14 rounded-full bg-statusRedBg border border-red-200 flex items-center justify-center text-statusRed">
          <WarningIcon className="w-7 h-7" />
        </div>

        <div className="space-y-1.5">
          <h1 className="text-2xl font-bold text-textPrimary">
            404 — Page Not Found
          </h1>
          <p className="text-sm text-textMuted max-w-xs mx-auto leading-relaxed">
            The page you requested does not exist or access is restricted.
          </p>
        </div>

        <div className="w-full bg-gray-50 border border-cardBorder rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between text-xs text-textMuted font-medium">
            <span>Auto logout safety timer</span>
            <span className="font-mono font-bold text-statusRed text-sm">
              {secondsLeft}s
            </span>
          </div>

          <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
            <div
              className="bg-brand h-full transition-all duration-1000 ease-linear rounded-full"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <p className="text-xs text-textMuted">
            You will be automatically logged out if you do not return to your dashboard.
          </p>
        </div>

        <div className="w-full space-y-3 pt-2">
          <Button variant="primary" onClick={handleReturnToDashboard}>
            Return to Dashboard
          </Button>

          <Button variant="outline" onClick={handleLogoutNow}>
            Log Out Now
          </Button>
        </div>
      </div>
    </AuthCard>
  );
};

export default Forbidden;
